import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import {
  catchError,
  combineLatest,
  defaultIfEmpty,
  distinctUntilChanged,
  filter,
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
  take,
} from 'rxjs';

import { isShortHaulFlight, PageMetaService, PriceCalendarCTAParams, PriceCalendarService } from '@fcom/common';
import { isShortHaulCountry } from '@fcom/common/utils/flight.utils';
import { DataCloudService } from '@fcom/common/datacloud/services/datacloud.service';
import { GlobalBookingTravelClass, LocationRouteCffService, stopPropagation } from '@fcom/core';
import { TripType } from '@fcom/core/interfaces/triptype.interface';
import { CmsOffer, Location } from '@fcom/core-api';
import { finShare } from '@fcom/rx';
import { LanguageService } from '@fcom/ui-translate';
import { DestinationSearchService } from '@fcom/destination-search/services/destination-search.service';

import { BlockOffer, BlockOfferData } from '../../interfaces/destination-promo.interface';
import { CheapestPriceForAllDestinationsService } from '../../services/cheapest-price/cheapest-price-for-all-destinations.service';
import { toPromoBlock } from '../../services/template/data-mappers';
import { computeCountryMinOfferMap, CountryMinOffer } from './cms-destination-category-list.utils';

type OfferType = 'destination' | 'country';

interface CmsOfferWithGeoLocation extends CmsOffer {
  geoLocation?: {
    countryCode?: string;
  };
}

interface OfferMeta {
  offer: CmsOfferWithGeoLocation;
  baseOffer: BlockOfferData;
  type: OfferType;
  code: string | null;
  countryCode: string | null;
}

interface ResolvedOffer {
  offerData: BlockOfferData;
  tripType: TripType;
  trackKey: string;
  showPriceCalendarButton: boolean;
}

@Component({
  selector: 'cms-destination-category-list',
  template: `
    <div class="grid-col-12 grid-max-width no-gutters category-grid">
      @for (item of resolvedOffers(); track item.trackKey) {
        <fin-block-offer
          class="category-tile"
          [offer]="item.offerData"
          [tripType]="item.tripType"
          [showPriceCalendarButton]="item.showPriceCalendarButton"
          (calendarClicked)="openPriceCalendar($event)"
        />
      }
    </div>

    <fcom-modal
      [open]="(calendarOpen$ | async) !== null"
      [fitToContent]="true"
      [buttons]="null"
      [roundedContent]="true"
      [fullHeight]="true"
      [extraWide]="true"
      (close)="closePriceCalendar()"
      data-testid="price-calendar-modal"
    >
      <fin-price-calendar
        [openWithParams$]="calendarOpen$"
        [showAddReturn]="true"
        [showSubtitle]="true"
        [ctaLabel]="'bookingSearch.search' | finLocalization"
        (modalClose)="closePriceCalendar()"
        (ctaClicked)="priceCalendarCTAClicked($event)"
      />
    </fcom-modal>
  `,
  styleUrls: ['./cms-destination-category-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CmsDestinationCategoryListComponent {
  private pageMetaService = inject(PageMetaService);
  private priceCalendarService = inject(PriceCalendarService);
  private dataCloudService = inject(DataCloudService);
  private locationRouteCffService = inject(LocationRouteCffService);
  private languageService = inject(LanguageService);
  private destinationSearchService = inject(DestinationSearchService);
  private cheapestPriceForAllDestinationsService = inject(CheapestPriceForAllDestinationsService);

  offers = input.required<CmsOffer[]>();

  calendarOpen$ = this.priceCalendarService.calendarOpen$;

  readonly origin = toSignal(
    this.pageMetaService.defaultOriginLocationCode$.pipe(
      filter(Boolean),
      switchMap((code) => this.locationRouteCffService.bestGuessFor(code, this.languageService.localeValue)),
      finShare()
    )
  );

  private readonly offersWithMeta = computed<OfferMeta[]>(() => {
    const offers = this.offers() ?? [];

    return offers.map((offer) => {
      const baseOffer = toPromoBlock(offer);

      const offerWithGeo = offer as CmsOfferWithGeoLocation;
      const countryCode = offerWithGeo.geoLocation?.countryCode?.toUpperCase() ?? null;

      const destinationCode = offer.defaultLocationCode ?? offer.target?.locationCode ?? null;

      return {
        offer: offerWithGeo,
        baseOffer,
        type: countryCode ? 'country' : 'destination',
        code: countryCode ? null : (destinationCode ?? baseOffer?.destination ?? null),
        countryCode,
      };
    });
  });

  private readonly allCodes = computed(() => {
    const codes = this.offersWithMeta()
      .filter((meta) => meta.type === 'destination' && !!meta.code)
      .map((meta) => meta.code);
    return [...new Set(codes)];
  });

  private readonly allCountryCodes = computed(() => {
    const countryCodes = this.offersWithMeta()
      .filter((meta) => meta.type === 'country' && !!meta.countryCode)
      .map((meta) => meta.countryCode);
    return [...new Set(countryCodes)];
  });

  private readonly countryDestinationsMap = toSignal(
    toObservable(this.allCountryCodes).pipe(
      distinctUntilChanged((prev, curr) => prev.join('|') === curr.join('|')),
      switchMap((countryCodes) => {
        if (countryCodes.length === 0) {
          return of({} as Record<string, string[]>);
        }

        return forkJoin(
          countryCodes.map((countryCode) =>
            this.destinationSearchService.getDestinationLocationCodesForCountry(countryCode).pipe(
              take(1),
              map((destinationCodes) => [countryCode, destinationCodes]),
              catchError(() => of([countryCode, []]))
            )
          )
        ).pipe(map((entries) => Object.fromEntries(entries) as Record<string, string[]>));
      })
    ),
    { initialValue: {} as Record<string, string[]> }
  );

  private readonly tripTypeResolutionCodes = computed<string[]>(() => {
    return this.allCodes();
  });

  private readonly allDestinationOffers = toSignal(
    this.cheapestPriceForAllDestinationsService.bothWayOffers().pipe(catchError(() => of([]))),
    { initialValue: [] }
  );

  private readonly countryTripTypeMap = computed<Record<string, TripType>>(() => {
    const origin = this.origin();
    const isOriginShortHaul = !!origin && isShortHaulCountry(origin.countryCode);
    const result: Record<string, TripType> = {};
    for (const countryCode of this.allCountryCodes()) {
      result[countryCode] = isOriginShortHaul && isShortHaulCountry(countryCode) ? TripType.ONEWAY : TripType.RETURN;
    }
    return result;
  });

  private readonly countryMinOfferMap = computed<Record<string, CountryMinOffer>>(() => {
    return computeCountryMinOfferMap(
      this.countryDestinationsMap(),
      this.allDestinationOffers(),
      this.countryTripTypeMap()
    );
  });

  private readonly destinationTripTypesReady = computed(() => {
    const tripTypeMap = this.tripTypeMap();
    return this.allCodes().every((code) => code in tripTypeMap);
  });

  private readonly countryDestinationsReadyMap = computed<Record<string, boolean>>(() => {
    const countryDestinationsMap = this.countryDestinationsMap();
    const result: Record<string, boolean> = {};

    for (const countryCode of this.allCountryCodes()) {
      result[countryCode] = countryCode in countryDestinationsMap;
    }

    return result;
  });

  private readonly tripTypeMap = toSignal(
    combineLatest([toObservable(this.origin), toObservable(this.tripTypeResolutionCodes)]).pipe(
      filter(([origin, codes]) => !!origin && codes.length > 0),
      distinctUntilChanged(
        ([prevOrigin, prevCodes], [currOrigin, currCodes]) =>
          prevOrigin?.locationCode === currOrigin?.locationCode &&
          prevCodes.length === currCodes.length &&
          prevCodes.every((c, i) => c === currCodes[i])
      ),
      switchMap(([origin, codes]) => this.resolveTripTypes(origin, codes))
    ),
    { initialValue: {} }
  );

  private resolveCountryOffer(meta: OfferMeta, index: number): ResolvedOffer {
    const tripType = meta.countryCode
      ? (this.countryTripTypeMap()[meta.countryCode] ?? TripType.RETURN)
      : TripType.RETURN;
    const minOffer = meta.countryCode ? this.countryMinOfferMap()[meta.countryCode] : undefined;
    const trackBase = meta.countryCode ?? meta.code ?? 'unknown';
    const contentId = meta.offer?.contentId ?? 'no-content-id';
    const trackId = `${trackBase}-${contentId}-${index}`;

    return {
      offerData: {
        ...meta.baseOffer,
        destination: minOffer?.destinationCode ?? meta.baseOffer.destination,
        onewayPrice: tripType === TripType.ONEWAY ? minOffer?.priceOffer : undefined,
        returnPrice: tripType === TripType.RETURN ? minOffer?.priceOffer : undefined,
        isShortHaul: tripType === TripType.ONEWAY,
      },
      tripType,
      trackKey: `country-${trackId}`,
      showPriceCalendarButton: false,
    };
  }

  private resolveDestinationOffer(
    meta: OfferMeta,
    tripTypeMap: Record<string, TripType>,
    index: number
  ): ResolvedOffer {
    const tripType = meta.code ? (tripTypeMap[meta.code] ?? TripType.RETURN) : TripType.RETURN;
    const contentId = meta.offer?.contentId ?? 'no-content-id';

    return {
      offerData: {
        ...meta.baseOffer,
        isShortHaul: tripType === TripType.ONEWAY,
      },
      tripType,
      trackKey: `destination-${meta.code ?? 'unknown'}-${contentId}-${index}`,
      showPriceCalendarButton: true,
    };
  }

  readonly resolvedOffers = computed<ResolvedOffer[]>(() => {
    const offersWithMeta = this.offersWithMeta();
    const tripTypeMap = this.tripTypeMap();
    const destinationTripTypesReady = this.destinationTripTypesReady();
    const countryDestinationsReadyMap = this.countryDestinationsReadyMap();

    const result: ResolvedOffer[] = [];

    for (let i = 0; i < offersWithMeta.length; i++) {
      const meta = offersWithMeta[i];

      if (meta.type === 'country') {
        if (meta.countryCode && !countryDestinationsReadyMap[meta.countryCode]) {
          continue;
        }
        result.push(this.resolveCountryOffer(meta, i));
      } else {
        // destination tile waits until all destination trip types are resolved
        if (!meta.code || !destinationTripTypesReady) {
          continue;
        }
        result.push(this.resolveDestinationOffer(meta, tripTypeMap, i));
      }
    }

    return result;
  });

  openPriceCalendar([event, destinationItem, travelClass, tripType]: [
    Event,
    BlockOffer,
    GlobalBookingTravelClass,
    TripType,
  ]): void {
    const origin = this.origin();
    if (!origin) {
      return;
    }

    stopPropagation(event);
    this.priceCalendarService.openPriceCalendar(
      origin.locationCode,
      destinationItem.destination,
      tripType,
      travelClass,
      destinationItem.title,
      destinationItem.discountCode
    );

    this.dataCloudService.allDestinationsPageDestinationClick(origin.locationCode, destinationItem.destination);
  }

  priceCalendarCTAClicked(params: PriceCalendarCTAParams): void {
    this.priceCalendarService.priceCalendarCTAClicked(params);
    this.closePriceCalendar();
  }

  closePriceCalendar(): void {
    this.priceCalendarService.closePriceCalendar();
  }

  private resolveTripTypes(
    origin: Location | undefined,
    destinationCodes: string[]
  ): Observable<Record<string, TripType>> {
    if (!origin || destinationCodes.length === 0) {
      return of({});
    }

    const locale = this.languageService.localeValue;

    return forkJoin(
      destinationCodes.map((code) =>
        this.locationRouteCffService.bestGuessFor(code, locale).pipe(
          take(1),
          map((dest) => [code, isShortHaulFlight(origin, dest) ? TripType.ONEWAY : TripType.RETURN]),
          defaultIfEmpty([code, TripType.RETURN]),
          catchError(() => of([code, TripType.RETURN]))
        )
      )
    ).pipe(map((entries) => Object.fromEntries(entries)));
  }
}
